/**
 * imgUrl, image URL helper
 *
 * Routes Supabase storage URLs through the render/image transform endpoint
 * (resize + WebP conversion). Standardised to ONE size (1200px wide, quality 75)
 * to keep Supabase transform billing predictable, Cloudflare caches the result
 * so each unique image is only transformed once per cache window.
 *
 * Unsplash URLs use their native resize params (no extra billing).
 * All other URLs pass through unchanged.
 *
 * Width default: 1200px, covers hero/showcase displays at retina density.
 * Override with { width } option when a specific size is needed.
 */
export function imgUrl(src, { width = 1200, height, quality = 75, resize } = {}) {
  if (!src || typeof src !== 'string') return '';

  // Supabase storage URL → swap to render/image endpoint.
  // NOTE: the Supabase transform defaults to resize=cover, which CROPS to fill the
  // requested width (with only width given it keeps the source height and chops the
  // sides). That's correct for fixed-aspect cards, but wrong for "show the whole
  // image" cases, pass `resize: 'contain'` to scale proportionally without cropping.
  // resize:'contain' ALSO needs an explicit `height` to actually scale
  // proportionally — with width only, the transform leaves height at the
  // source's native pixel size instead of computing it from the aspect ratio,
  // so a wide image (e.g. a logo lockup) comes back squished into a near-square
  // box. Found 2026-09-12 on the site header logo. Pass `height` whenever the
  // source aspect ratio is known.
  const STORAGE_SEGMENT = '/storage/v1/object/public/';
  if (src.includes('supabase.co') && src.includes(STORAGE_SEGMENT)) {
    const transformed = src.replace(STORAGE_SEGMENT, '/storage/v1/render/image/public/');
    // The source may already carry a query (e.g. a ?v= cache-buster). Appending
    // with a second '?' glues the params into the previous value, so Supabase
    // silently drops width/quality/format. Use '&' when a query already exists.
    const sep = transformed.includes('?') ? '&' : '?';
    return `${transformed}${sep}width=${width}${height ? `&height=${height}` : ''}&quality=${quality}&format=webp${resize ? `&resize=${resize}` : ''}`;
  }

  // Unsplash → use their built-in transformation params (free, no billing)
  if (src.includes('unsplash.com')) {
    try {
      const url = new URL(src);
      url.searchParams.set('w', String(width));
      url.searchParams.set('q', String(quality));
      url.searchParams.set('fm', 'webp');
      url.searchParams.set('fit', 'crop');
      return url.toString();
    } catch {
      return src;
    }
  }

  return src;
}
